import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, hasAdminToken } from "../lib/api";
import { Star, Trash2 } from "lucide-react";

export default function Community() {
  const { t, i18n } = useTranslation();
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.activityFeed(50).then((d) => { setFeed(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    setIsAdmin(hasAdminToken());
    load();
  }, [load]);

  const localeMap = { es: "es-CL", pt: "pt-BR", en: "en-US" };
  const dateLocale = localeMap[i18n.language] || "es-CL";

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta reseña definitivamente?")) return;
    setDeleting(id);
    try {
      await api.deleteReviewAdmin(id);
      setFeed((f) => f.filter((r) => r.id !== id));
    } catch (ex) {
      alert(ex?.response?.data?.detail || "No pudimos eliminar la reseña.");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-8" data-testid="community-page">
      <header className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">{t("community.eyebrow")}</div>
        <h1 className="mt-1 font-display text-5xl sm:text-6xl uppercase tracking-tighter leading-none">{t("community.title")}</h1>
        <p className="mt-3 text-[#A3A3A3]">{t("community.desc")}</p>
      </header>

      {loading ? (
        <div className="beer-pour" />
      ) : feed.length === 0 ? (
        <div className="border-2 border-dashed border-[#333] p-10 text-center text-[#A3A3A3] font-mono uppercase text-xs tracking-widest">
          {t("community.empty")}
        </div>
      ) : (
        <div className="space-y-3">
          {feed.map((r) => (
            <article
              key={r.id}
              className="bg-[#1A1A1A] border-2 border-[#333] p-4 hover:border-[#FEBA01] transition-colors"
              data-testid={`feed-review-${r.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#FEBA01] text-black flex items-center justify-center font-display text-lg shrink-0">
                  {String(r.user_handle || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <div>
                      <span className="font-display text-lg uppercase">{r.user_handle || "anonimo"}</span>
                      <span className="text-[#A3A3A3]"> {t("community.at")} </span>
                      <Link
                        to={`/local/${r.venue_id}`}
                        className="text-[#FEBA01] hover:underline font-bold"
                      >
                        {r.venue_name || "—"}
                      </Link>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.rating != null && (
                        <div className="flex items-center gap-1 text-[#FEBA01] text-sm">
                          <Star size={12} className="fill-[#FEBA01]" /> {r.rating}
                        </div>
                      )}
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => handleDelete(r.id)}
                          disabled={deleting === r.id}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 border border-red-500/60 text-red-400 hover:bg-red-500 hover:text-white transition disabled:opacity-40"
                          data-testid={`feed-review-delete-${r.id}`}
                          title="Eliminar (admin)"
                        >
                          <Trash2 size={11} /> {deleting === r.id ? "…" : "Eliminar"}
                        </button>
                      )}
                    </div>
                  </div>
                  {r.text && (
                    <p className="mt-2 text-sm text-[#F5F5F5] whitespace-pre-line">
                      &ldquo;{r.text}&rdquo;
                    </p>
                  )}
                  {r.tags?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {r.tags.map((tg) => (
                        <span
                          key={tg}
                          className="px-2 py-0.5 bg-[#0A0A0A] border border-[#333] text-[#FEBA01] font-mono text-[10px] uppercase tracking-wider"
                        >
                          {tg}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                    {new Date(r.created_at).toLocaleString(dateLocale)}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
